import React, { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import Image from "react-bootstrap/Image";
import { Button } from "react-bootstrap";
import { ALLOWED_IMAGE_TYPES, CONSTANTS } from "../../utils/Constant/Constant";
import compressImage from "./Compressimage";

const ITEM_TYPE = "SESSION";


const MAX_FILE_SIZE = 1024 * 1024; // 1MB

const readOrCompressFile = async (file, maxSizeBytes) => {
  if (file.size > maxSizeBytes) {
    console.log(`[SessionCard] File size ${file.size} exceeds ${maxSizeBytes}. Compressing...`);
    const compressed = await compressImage(file, maxSizeBytes);
    console.log("[SessionCard] Compression done:", compressed);
    return {
      base64Image: compressed.base64,
      fileType: compressed.fileType,
      mediaType: compressed.mediaType,
      fileName: file.name.replace(/\.[^/.]+$/, ".jpg"),
    };
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      let base64Image = reader.result;
      if (base64Image && base64Image.startsWith("data:")) {
        base64Image = base64Image.split(",")[1];
      }
      resolve({
        base64Image,
        fileType: file.type,
        mediaType: file.type.split("/")[0],
        fileName: file.name,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default function SessionCard({
  session,
  index,
  activeSessionId,
  placeholderFrontImage,
  handleSessionClick,
  handleImageRemoveSession,
  handleImageChangeSession,
  handleFileInputReset,
  handleRemoveSession,
  setSessions,
  setForm,
  form,
  moveSession,
  setToastConfig
}) {
  const ref = useRef(null);

  // Track session interaction start time
  const sessionInteractionStart = useRef(Date.now());

  // Drop target
  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover(item) {
      if (item.index !== index) {
        moveSession(item.index, index);
        item.index = index;
      }
    },
  });

  // Drag source
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  // Track session click
  const handleSessionClickWithTracking = (sessionId) => {
    handleSessionClick(sessionId);
    sessionInteractionStart.current = Date.now();
  };

  // Track image removal
  const handleImageRemoveWithTracking = (sessionId) => {
    handleImageRemoveSession(sessionId);
    sessionInteractionStart.current = Date.now();
  };

  // Validates type, then compresses if needed
  const handleImageChangeWithTracking = async (event, sessionId) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Block GIFs and non-static image types
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      event.target.value = "";
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        heading: "Invalid File Type",
        message: "Only static images are allowed (JPEG, PNG, WEBP). GIFs and animations are not supported.",
      });

      return;
    }

    try {
      const { base64Image, fileType, mediaType, fileName } = await readOrCompressFile(file, MAX_FILE_SIZE);

      // Pass processed data directly into form state
      setForm((prevData) => ({
        ...prevData,
        clientWorkoutPlanSessionRequests: prevData.clientWorkoutPlanSessionRequests.map((s) =>
          s.clientWorkoutPlanSessionId === sessionId
            ? {
                ...s,
                sessionPhotoFileName: fileName,
                sessionPhotoMediaType: mediaType,
                sessionPhotoFileType: fileType,
                sessionPhotoBase64: base64Image,
              }
            : s
        ),
      }));

      sessionInteractionStart.current = Date.now();
    } catch (err) {
      console.error("[SessionCard] Image processing failed:", err);
      event.target.value = "";
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        heading: "Upload Failed",
        message: "Failed to process image. Please try another file.",
      });
    }
  };

  const handleFileInputResetWithTracking = (elementId) => {
    handleFileInputReset(elementId);
    sessionInteractionStart.current = Date.now();
  };

  // Track session name change
  const handleSessionNameChange = (e) => {
    const updatedSessions = form?.clientWorkoutPlanSessionRequests?.map((s) =>
      s.clientWorkoutPlanSessionId === session.clientWorkoutPlanSessionId
        ? { ...s, name: e.target.value }
        : s
    );
    setSessions(updatedSessions);

    setForm((prevForm) => {
      const updatedSessions = prevForm?.clientWorkoutPlanSessionRequests?.map((sessions) => {
        if (sessions.clientWorkoutPlanSessionId === session.clientWorkoutPlanSessionId) {
          return {
            ...sessions,
            sessionName: e.target.value,
          };
        }
        return sessions;
      });

      return {
        ...prevForm,
        clientWorkoutPlanSessionRequests: updatedSessions,
      };
    });

    sessionInteractionStart.current = Date.now();
  };

  // Track session removal
  const handleRemoveSessionWithTracking = (sessionId) => {
    handleRemoveSession(sessionId);
    sessionInteractionStart.current = Date.now();
  };

  return (
    <li
      ref={ref}
      key={session.clientWorkoutPlanSessionId}
      className={`session-item ${
        activeSessionId === session.clientWorkoutPlanSessionId ? "active" : ""
      }`}
      style={{ opacity: isDragging ? 0.5 : 1, cursor: "grab" }}
      onClick={() => handleSessionClickWithTracking(session.clientWorkoutPlanSessionId)}
    >
      <div>
        <div className="mx-3 mt-1 image-container-sessions position-relative profile-image-container">
          {session?.sessionPhotoBase64 ? (
            <Image
              src={`data:${session?.sessionPhotoFileType};base64,${session?.sessionPhotoBase64}`}
              alt="Profile Picture"
              className="w-100 h-100 rounded-3"
            />
          ) : (
            <Image
              src={placeholderFrontImage}
              alt="Default Profile"
              className="w-100 h-100 rounded-3 p-1"
            />
          )}

          {session?.sessionPhotoBase64 && (
            <Button
              variant="link"
              className="position-absolute m-2 p-0 icon-position-session"
              onClick={(e) => {
                e.stopPropagation();
                handleImageRemoveWithTracking(session.clientWorkoutPlanSessionId);
              }}
            >
              <i
                className="bi bi-x-circle-fill"
                style={{ fontSize: "30px", color: "red" }}
              ></i>
            </Button>
          )}

          {!session?.sessionPhotoBase64 && (
            <Button
              variant="link"
              className="position-absolute m-2 p-0 icon-position-session"
              onClick={(e) => {
                e.stopPropagation();
                document
                  .getElementById(
                    `workoutsession-${session.clientWorkoutPlanSessionId}`
                  )
                  .click();
              }}
            >
              <i
                className="bi bi-plus-circle-fill"
                style={{ fontSize: "30px", color: "green" }}
              ></i>
            </Button>
          )}

          {/* accept only static image types — no gif */}
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            id={`workoutsession-${session.clientWorkoutPlanSessionId}`}
            className="mt-2"
            onChange={(e) =>
              handleImageChangeWithTracking(e, session.clientWorkoutPlanSessionId)
            }
            style={{ display: "none" }}
            onClick={() =>
              handleFileInputResetWithTracking(
                `workoutsession-${session.clientWorkoutPlanSessionId}`
              )
            }
          />
        </div>
      </div>

      <input
        type="text"
        value={session.sessionName}
        onChange={handleSessionNameChange}
        className="session-name-input"
      />

      <span
        className="remove-session"
        onClick={(e) => {
          e.stopPropagation();
          handleRemoveSessionWithTracking(session.clientWorkoutPlanSessionId);
        }}
      >
        X
      </span>
    </li>
  );
}
